import { FunctionComponent } from 'react';
import Head from 'next/head';
import { H1, H2, H4, Text } from 'components/Typography/Typography';
import { Layout, LayoutWrapper } from 'components/Layout/Layout';
import styled, { css } from 'styled-components';
import { Header } from 'components/Layout/Header';
import { Footer } from 'components/Layout/Footer';

const CookiePolicy: FunctionComponent = () => {
  return (
    <Layout>
      <Head>
        <title>The Earth Prize - Cookie Policy</title>
        <meta
          name="description"
          content="The Earth Prize is the environmental sustainability competition for teenage students. The Earth Prize will recognize the students and schools with the best solutions to accelerate positive change towards environmental sustainability, as judged by The Earth Prize Adjudicating Panel."
        />
        <meta property="og:title" content="The Earth Prize - Cookie Policy" />
        <meta
          property="og:description"
          content="The Earth Prize is the environmental sustainability competition for teenage students. The Earth Prize will recognize the students and schools with the best solutions to accelerate positive change towards environmental sustainability, as judged by The Earth Prize Adjudicating Panel."
        />
        <meta property="og:image" content="/images/cover.jpg" />
      </Head>
      <Header />
      <CookiePolicyWrapper>
        <H1>
          THE EARTH PRIZE
          <br /> COOKIE POLICY
        </H1>
        <Text>
          <b>Effective from:</b> 1st February, 2024
        </Text>
        <br />
        <Text>
          <i>
            We use cookies on this website. If you would like to know more about
            cookies and how we use them, please read this cookie policy. You can
            always contact us if you have any questions about our cookies.
          </i>
        </Text>
        <br />
        <a href="#WHO-CONTROLS">
          <H4>1. WHO CONTROLS COOKIES ON THIS WEBSITE?</H4>
        </a>
        <a href="#WHAT-SHOULD-YOU-KNOW">
          <H4>2. WHAT SHOULD YOU KNOW ABOUT COOKIES?</H4>
        </a>
        <a href="#WHAT-COOKIES">
          <H4>3. WHAT COOKIES DO WE USE?</H4>
        </a>
        <a href="#COOKIE-CONSENT">
          <H4>4. COOKIE CONSENT</H4>
        </a>
        <a href="#HOW-TO-REFUSE">
          <H4>5. HOW TO REFUSE COOKIES?</H4>
        </a>
        <a href="#DO-WE-USE-GOOGLE-ANALYTICS">
          <H4>6. DO WE USE GOOGLE ANALYTICS?</H4>
        </a>
        <a href="#DO-WE-USE-GOOGLE-reCAPCHA">
          <H4>7. DO WE USE GOOGLE reCAPCHA?</H4>
        </a>
        <a href="#HOW-TO-CONTACT">
          <H4>8. HOW TO CONTACT US?</H4>
        </a>
        <br />
        <H2>
          <span id="WHO-CONTROLS" />
          1. WHO CONTROLS COOKIES ON THIS WEBSITE?
        </H2>
        <Text>
          We are called The Earth Foundation. We are based in Switzerland, at
          the following address: Boulevard du théâtre 5, 1204 Genève,
          Switzerland. We have a registration number that is CHE-439.560.973. We
          are the owners of this website. It means that we make all decisions
          about its design and cookies.
        </Text>
        <br />
        <H2>
          <span id="WHAT-SHOULD-YOU-KNOW" />
          2. WHAT SHOULD YOU KNOW ABOUT COOKIES?
        </H2>
        <Text>
          A cookie is a small computer file consisting of letters and numbers.
          When you visit any website on the Internet, that website may send a
          cookie to your browser. That cookie will be stored until it expires or
          you delete it.
          <br />
          Cookies are used to recognize your device and collect certain
          information about your use of websites. Thus, over time, cookies allow
          websites to “remember” your actions and preferences.
          <br />
          There are several types of cookies. For example:
        </Text>
        <br />
        <ul>
          <li>
            If a cookie stays in your browser until you delete it, it is called
            a ‘persistent cookie’;
          </li>
          <li>
            If a cookie remains valid until you close your browse, such a cookie
            is called a ‘session cookie’:
          </li>
          <li>
            Some cookies have a pre-programmed expiration time like 1 day or 1
            month;
          </li>
          <li>
            If our website sends a cookie to your browser, such a cookie is
            called ‘a first-party cookie’; and
          </li>
          <li>
            If our partners send a cookie to your browser while you are using
            this website, such a cookie is called ‘a third-party cookie’.
          </li>
        </ul>
        <br />
        <H2>
          <span id="WHAT-COOKIES" />
          3. WHAT COOKIES DO WE USE?
        </H2>
        <Text>
          Some cookies are necessary to ensure that the website functions
          properly and stays safe. We call such cookies ‘essential cookies’. We
          use such cookies to protect the website and make sure that you can use
          its all functionalities.
          <br />
          We also use statistics cookies that allow us to generate reports about
          how you use the website. For example, we may see how you behave on the
          website, what pages you click on, where you go from the website, and
          so on. Below, you can find a list of cookies that we use on this
          website, including their purpose and expiration time:
        </Text>
        <TableWrapper>
          <table>
            <thead>
              <tr>
                <th colSpan={5}>Statistics cookies</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <b>Name</b>
                </td>
                <td>
                  <b>Type</b>
                </td>
                <td>
                  <b>Provider (location)</b>
                </td>
                <td>
                  <b>Expiration</b>
                </td>
                <td>
                  <b>Purpose</b>
                </td>
              </tr>
              <tr>
                <td>_ga</td>
                <td>First-party HTTP cookie</td>
                <td>
                  <a href="/">theearthprize.org</a> (Switzerland)
                </td>
                <td>2 years</td>
                <td>
                  The cookie is used to set a unique ID that is used to generate
                  statistical data on how you use the Website.
                </td>
              </tr>
              <tr>
                <td>_gat</td>
                <td>First-party HTTP cookie</td>
                <td>
                  <a href="/">theearthprize.org</a> (Switzerland)
                </td>
                <td>1 day</td>
                <td>
                  The cookie is used by Google Analytics to throttle request
                  rate.
                </td>
              </tr>
              <tr>
                <td>_gid</td>
                <td>First-party HTTP cookie</td>
                <td>
                  <a href="/">theearthprize.org</a> (Switzerland)
                </td>
                <td>1 day</td>
                <td>
                  The cookie is used to set a unique ID that is used to generate
                  statistical data on how you use the Website.
                </td>
              </tr>
              <tr>
                <td>collect</td>
                <td>Third-party Pixel cookie</td>
                <td>
                  <a href="/">theearthprize.org</a> (Switzerland)
                </td>
                <td>End of session</td>
                <td>
                  The cookie is used to send data to Google Analytics about your
                  device and behavior. It tracks you across devices and
                  marketing channels.
                </td>
              </tr>
              <tr>
                <td>_Secure-next-auth.session-token</td>
                <td>First-party Persistent cookie</td>
                <td>
                  <a href="/">theearthprize.org</a> (Switzerland)
                </td>
                <td>15 minutes</td>
                <td>
                  The cookie is used to for user for setting user session after
                  log in
                </td>
              </tr>
              <tr>
                <td>_Secure-next-auth.callback-url</td>
                <td>First-party Session cookie</td>
                <td>
                  <a href="/">theearthprize.org</a> (Switzerland)
                </td>
                <td>15 minutes</td>
                <td>
                  The cookie is used to for user for setting user session after
                  log in
                </td>
              </tr>
              <tr>
                <td>_Host-next-auth.csrf-token</td>
                <td>First-party Session cookie</td>
                <td>
                  <a href="/">theearthprize.org</a> (Switzerland)
                </td>
                <td>15 minutes</td>
                <td>
                  The cookie is used to for user for setting user session after
                  log in
                </td>
              </tr>
            </tbody>
          </table>
        </TableWrapper>
        <br />
        <H2>
          <span id="COOKIE-CONSENT" />
          4. COOKIE CONSENT
        </H2>
        <Text>
          When you visit the website for the first time, we may ask you to
          provide us with your consent to our use of all cookies via a cookie
          consent banner. If you choose not to provide your consent or you are
          not old enough to give a valid consent, we will serve you our
          essential cookies only. Please note that we may not be able to provide
          you with the best possible user experience on the Website if not all
          cookies are enabled.
        </Text>
        <br />
        <H2>
          <span id="HOW-TO-REFUSE" />
          5. HOW TO REFUSE COOKIES?
        </H2>
        <Text>
          When we ask you to provide your consent to our use of non-essential
          cookies, you have a choice not to provide such consent. If you would
          like to refuse our use of non-essential cookies later, you can do it
          at any time by disabling cookies in your browser or device. You can
          find more information in the “help” pages of your browser. The links
          below may be useful as well:
        </Text>
        <br />
        <ul>
          <li>
            Apple Safari:{' '}
            <a
              href="https://support.apple.com/en-gb/guide/safari/manage-cookies-and-website-data-sfri11471/mac"
              target="_blank"
              rel="noreferrer"
            >
              https://support.apple.com/en-gb/guide/safari/manage-cookies-and-website-data-sfri11471/mac
            </a>
          </li>
          <li>
            Google Chrome:{' '}
            <a
              href="https://support.google.com/chrome/answer/95647"
              target="_blank"
              rel="noreferrer"
            >
              https://support.google.com/chrome/answer/95647
            </a>
          </li>
          <li>
            Firefox:{' '}
            <a
              href="https://support.mozilla.org/en-US/kb/enable-and-disable-cookies-website-preferences"
              target="_blank"
              rel="noreferrer"
            >
              https://support.mozilla.org/en-US/kb/enable-and-disable-cookies-website-preferences
            </a>
          </li>
          <li>
            Internet Explorer:{' '}
            <a
              href="https://support.microsoft.com/en-gb/help/17442/windows-internet-explorer-delete-manage-cookies"
              target="_blank"
              rel="noreferrer"
            >
              https://support.microsoft.com/en-gb/help/17442/windows-internet-explorer-delete-manage-cookies
            </a>
          </li>
          <li>
            Microsoft Edge:{' '}
            <a
              href="https://support.microsoft.com/en-us/help/4468242/microsoft-edge-browsing-data-and-privacy-microsoft-privacy"
              target="_blank"
              rel="noreferrer"
            >
              https://support.microsoft.com/en-us/help/4468242/microsoft-edge-browsing-data-and-privacy-microsoft-privacy
            </a>
          </li>
        </ul>
        <br />
        <H2>
          <span id="DO-WE-USE-GOOGLE-ANALYTICS" />
          6. DO WE USE GOOGLE ANALYTICS?
        </H2>
        <Text>
          Yes, we use Google Analytics on this website. It is a service provided
          by Google LLC located in the United States (“<b>Google</b>”). Google
          helps us to analyze your use of this website and create statistical
          reports about your use of the website. To achieve this, Google uses
          cookies. The cookies served by Google Analytics are anonymous
          first-party cookies (in section 3, you can find more information) that
          do not allow us to identify you — you remain anonymous. The
          information generated by Google’s cookies will be sent to Google
          servers in the United States. To ensure your privacy, your IP address
          will be anonymized and Google will not combine your IP address with
          other information Google holds about you. Thus, Google will not be
          able to identify you. In certain cases (for example, when Google’s
          partner conducts services on behalf of Google), Google may transfer
          the information to third parties.
          <br />
          If you would like to read more about Google Analytics’ privacy
          practices, please visit{' '}
          <a
            href="https://support.google.com/analytics/answer/6004245"
            target="_blank"
            rel="noreferrer"
          >
            https://support.google.com/analytics/answer/6004245
          </a>
          . If you would like to opt out from Google Analytics, you can do so by
          installing a Google Analytics opt-out browser add-on available at{' '}
          <a
            href="https://tools.google.com/dlpage/gaoptout?hl=en"
            target="_blank"
            rel="noreferrer"
          >
            https://tools.google.com/dlpage/gaoptout?hl=en
          </a>
          .
        </Text>
        <br />
        <H2>
          <span id="DO-WE-USE-GOOGLE-reCAPCHA" />
          7. DO WE USE GOOGLE reCAPCHA?
        </H2>
        <Text>
          Yes, we use reCAPCHA on this website. It helps us to verify whether
          you are a human being and not a robot. We need such information to
          protect our website against spam and abuse. When you use reCAPCHA,
          Google will collect some data from you, such as:{' '}
        </Text>
        <br />
        <ul>
          <li>
            All cookies set to your browser by Google in the last 6 months;
          </li>
          <li>Information about your mouse clicks;</li>
          <li>CSS information;</li>
          <li>Date;</li>
          <li>Browser language;</li>
          <li>Any plug-in installed in the browser; and</li>
          <li>Javascript objects.</li>
        </ul>
        <br />
        <Text>
          Such data will be sent to Google servers located in the United States.
          If you would like to read more about Google reCAPTCHA and Google’s
          privacy policy, please visit the following links:{' '}
          <a
            href="https://tools.google.com/dlpage/gaoptout?hl=en"
            target="_blank"
            rel="noreferrer"
          >
            https://www.google.com/intl/de/policies/privacy/
          </a>{' '}
          and{' '}
          <a
            href="https://tools.google.com/dlpage/gaoptout?hl=en"
            target="_blank"
            rel="noreferrer"
          >
            https://www.google.com/recaptcha/intro/v3.html
          </a>
          .
        </Text>
        <br />
        <H2>
          <span id="HOW-TO-CONTACT" />
          6. HOW TO CONTACT US?
        </H2>
        <Text>
          If you have any questions about our cookies, please contact us by
          using the following contact details:
        </Text>
        <br />
        <AddressWrapper>
          <div>
            <Text>
              <b>Email:</b>
              <br />
              <b>Postal address:</b>
            </Text>
          </div>
          <div>
            <Text>
              info@theearthprize.org
              <br />
              The Earth Foundation
              <br />
              Boulevard du théâtre 5
              <br />
              1204 Genève
              <br />
              Switzerland
            </Text>
          </div>
        </AddressWrapper>
      </CookiePolicyWrapper>
      <Footer fullHeight />
    </Layout>
  );
};

const CookiePolicyWrapper = styled(LayoutWrapper)`
  ${({ theme: { Colors, Consts } }) => css`
    padding: 246px 80px 142px;
    background-color: ${Colors.CookiePolicyWrapperBackgroundColor};
    @media (max-width: ${Consts.tabletBreakPoint}) {
      padding: 100px 30px 121px;
    }

    h1 {
      margin-bottom: 34px;
      color: ${Colors.CookiePolicyWrapperColor};
      @media (max-width: ${Consts.tabletBreakPoint}) {
        margin-bottom: 20px;
        font-size: 31px;
      }
    }

    h2 {
      margin-bottom: 34px;
      font-size: 28px;
      color: ${Colors.CookiePolicyWrapperColor};

      > span {
        position: relative;
        top: -100px;
      }

      @media (max-width: ${Consts.tabletBreakPoint}) {
        margin-bottom: 20px;
        font-size: 20px;
      }
    }

    > a {
      text-decoration: underline;
      color: ${Colors.CookiePolicyWrapperColor};

      h4 {
        font-size: 18px;
        color: ${Colors.CookiePolicyWrapperColor};
      }
    }

    p {
      margin-bottom: 0;
      color: ${Colors.CookiePolicyWrapperColor};

      a {
        color: ${Colors.CookiePolicyWrapperColor};
        text-decoration: underline;
      }

      > span {
        text-decoration: underline;
        cursor: pointer;
      }
    }

    ul {
      padding-left: 30px;
      font-size: 14px;

      > li {
        color: ${Colors.CookiePolicyWrapperColor};

        a {
          color: ${Colors.CookiePolicyWrapperColor};
          text-decoration: underline;
        }

        > span {
          text-decoration: underline;
          cursor: pointer;
        }
      }
    }
  `}
`;

const AddressWrapper = styled.div`
  display: flex;
  align-items: flex-start;

  > div {
    margin-right: 20px;
  }
`;

const TableWrapper = styled.div`
  ${({ theme: { Consts, Colors } }) => css`
    width: 100%;
    overflow: scroll;

    @media (max-width: ${Consts.tabletBreakPoint}) {
      width: calc(100% + 60px);
      margin-left: -30px;
      padding: 0 30px;
    }

    table {
      border-collapse: collapse;
      margin-top: 20px;
      min-width: 600px;

      @media (max-width: ${Consts.tabletBreakPoint}) {
        min-width: 750px;
      }
    }

    td,
    th {
      border: 1px solid ${Colors.CookiePolicyWrapperColor};
      padding: 0.5rem;
      text-align: center;
      color: ${Colors.CookiePolicyWrapperColor};
      font-size: 14px;

      a {
        color: ${Colors.CookiePolicyWrapperColor};
        text-decoration: underline;
      }

      &:nth-child(2) {
        width: 180px;
      }

      @media (max-width: ${Consts.tabletBreakPoint}) {
        font-size: 15px;
        line-height: 22px;
        letter-spacing: 0.5px;
      }
    }

    th {
      font-size: 17px;
    }
  `}
`;

export default CookiePolicy;
